import { createClient } from '@/shared/api';
import DOWNLOAD_PDF_REPORT_MUTATIONS from '@/entities/report/api/mutations/downloadPdfReport';
import type {
  DownloadPdfReportMutation,
  DownloadPdfReportMutationVariables,
} from '@/shared/api/generated/graphql';

export type DownloadPdfReportResult = {
  isSuccess: boolean;
  message: string;
  url: string;
};

/**
 * Requests the download payload for a generated PDF report. Runs the download mutation with markAsDownloaded forced to false and throws the server message unless it reports success.
 *
 * @param studyFid - Friendly id of the study the report belongs to.
 * @param reportType - Type of report to download.
 * @param reportId - Identifier of the specific report.
 * @param eventFid - Friendly id of the event for event-scoped reports.
 * @returns A promise resolving to the full mutation result carrying the download payload.
 */
const handleDownloadPdfReport = async ({
  studyFid,
  reportType,
  reportId,
  eventFid,
}: {
  studyFid?: number;
  reportType?: string;
  reportId?: number;
  eventFid?: number;
}) => {
  const client = await createClient();
  const downloadPdfReportResult = await client.mutate<
    DownloadPdfReportMutation,
    DownloadPdfReportMutationVariables
  >({
    mutation: DOWNLOAD_PDF_REPORT_MUTATIONS,
    variables: {
      studyFid,
      reportType,
      reportId,
      eventFid,
      markAsDownloaded: false,
    },
  });
  const { data } = downloadPdfReportResult;
  const { downloadPdfReport } = data;
  if (!downloadPdfReport?.isSuccess) {
    throw new Error(downloadPdfReport?.message);
  } else {
    return downloadPdfReportResult;
  }
};

export default handleDownloadPdfReport;
